import '/assets/styles/modals/mobile-menu.scss';
import classNames from 'classnames';
import { useMemo } from 'react';

// Hooks
import useAccountMenuItems from '/hooks/account-menu-items';

// Partials
import Link from '@wearetla/tla-essentials-tools/partials/link';
import Icon from '@wearetla/tla-essentials-tools/partials/icon';

// Context
import { useAuth } from '@wearetla/tla-essentials-tools/utilities/auth';
import { useNavigator } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useGlobalState } from '@wearetla/tla-essentials-tools/utilities/global-state';
import { useTranslation } from '/utilities/i18n';

// Functions
import blockOverflow from '@wearetla/tla-essentials-tools/functions/block-overflow';

const MobileMenuModal = ({ closeBtn, close }) => {
	const { t } = useTranslation();
	const accountMenuItems = useAccountMenuItems();
	const { eventCategories } = useGlobalState();
	const { userData, loggedIn } = useAuth();
	const { openModal } = useModals();
	const { routes } = useNavigator();

	const menuItems = useMemo(() => {
		const accountItems = accountMenuItems.filter((i) => i.route?.requiresLogin);
		const nonAccountItems = accountMenuItems.filter((i) => !i.route?.requiresLogin);

		return [
			...(loggedIn
				? accountItems
				: [
						{
							tag: 'button',
							type: 'button',
							onClick: () => {
								openModal('auth');
							},
							title: t('Üye Ol / Giriş Yap'),
							icon: 'user',
						},
					]),
			{
				tag: 'link',
				route: routes.page,
				params: { slug: 'hakkimizda', id: 1 },
				icon: 'info',
				title: t('Hakkımızda'),
			},
			{
				tag: 'link',
				route: routes.contact,
				icon: 'phone',
				title: routes.contact.head.title,
			},
			...nonAccountItems,
		];
	}, [accountMenuItems, loggedIn, openModal, routes, t]);

	return (
		<>
			{closeBtn}
			<div className="modal-body">
				<strong className="menu-title">
					{userData ? <>{t('Merhaba')} {userData?.name}</> : <>{t('Menü')}</>},
				</strong>
				<div className="menu-categories">
					<strong className="categories-title">{t('Kategoriler')}</strong>
					<ul className="categories-list">
						{eventCategories.map((category, nth) => (
							<li className="categories-item" key={category?.id ?? nth}>
								<Link
									className="item-link"
									href="eventsWithLocation"
									params={{ location: 'tumu', query: { type: category.id }}}
									onClick={() => {
										close();
									}}
								>
									{category.name}
								</Link>
							</li>
						))}
					</ul>
				</div>
				<ul className="menu-list">
					{menuItems.map((item, nth) => (
						<MenuItem item={item} close={close} key={nth} />
					))}
				</ul>
			</div>
		</>
	);
};

export const MenuItem = ({ item, close }) => {
	const { tag, active, className: rawClassName, title, icon, restricted, ...props } = item ?? {};
	const ElemTag = useMemo(() => {
		switch (tag) {
			case 'button':
				return 'button';
			default:
				return Link;
		}
	}, [tag]);

	const className = useMemo(
		() =>
			classNames('item-link', rawClassName, {
				active: active,
			}),
		[rawClassName, active]
	);

	return (
		<li className={classNames('menu-item', { restricted })}>
			{item && (
				<ElemTag
					onClick={() => {
						(
							props.onClick ??
							(() =>
								new Promise((resolve) => {
									resolve();
								}))
						)().then(() => {
							blockOverflow('modals', false, true);
							window.scrollTo(0,0);
							close();
						});
					}}
					className={className}
					{...props}
				>
					<span className="item-contentwrap">
						{!!icon && <Icon className="item-icon" name={icon} />}
						{title}
					</span>
				</ElemTag>
			)}
		</li>
	);
};

MobileMenuModal.modalConfig = {
	containerClass: 'modal-mobile-menu mobile-full',
};

export default MobileMenuModal;
